/// <amd-module name="Repos/Discussion/Editor/Widgets/AddCommentGutterWidget" />
import * as React from "react";
import * as ReactDOM from "react-dom";
import { DiffViewerOrientation } from "Repos/Common/Contracts/VersionControlViewModel";
import { IDiffMonacoDiscussionWidget, ILineInfo, LineInfoType } from "Repos/Discussion/Common/DiscussionContracts";
import { AddCommentButton } from "../Components/AddCommentButton";
import { AddBaseWidget } from "./AddBaseWidget";
import { EmptyDiffDiscussionWidget, isMe as isAddCommentWidget } from "./AddCommentWidget";

export interface IAddCommentGutterWidgetProps {
    addToOriginalDiff: boolean;
    diffOrientation: DiffViewerOrientation;
    onAddDiscussion: (lineInfo: ILineInfo, addToOriginalDiff: boolean) => void;
}

/**
 * Gets a gutter widget used to start a discussion thread on a particular line.
 * The widget targets a diff editor and we return widget registrations both for original and modified editors.
 */
export function getAddCommentGutterRegistration(props: IAddCommentGutterWidgetProps): IDiffMonacoDiscussionWidget<AddCommentGutterWidget> {
    const { addToOriginalDiff } = props;
    return {
        ...EmptyDiffDiscussionWidget,
        addToOriginalDiff,
        addWidget: (editor, diffEditor?) => {
            if (diffEditor) {
                const isOriginalDiff = diffEditor.getOriginalEditor() === editor;
                if (isOriginalDiff !== addToOriginalDiff) {
                    return undefined;
                }
            }

            const widget = new AddCommentGutterWidget(editor, props);
            editor.addOverlayWidget(widget);
            return widget;
        },
        removeWidget: (widget, editor) => {
            editor.removeOverlayWidget(widget);
            widget.dispose();
        },
        widgetId: `add-comment-gutter-widget-${addToOriginalDiff ? "original" : "modified"}`
    };
}

const hiddenTopStyle = "-10000px";
const componentType = ":AddCommentGutterWidget";

function isMe(id: any): boolean {
    return typeof id === "string" && id.indexOf(componentType) > 0;
}

class AddCommentGutterWidget extends AddBaseWidget<IAddCommentGutterWidgetProps> implements monaco.editor.IOverlayWidget {
    private lineNumber = 0;

    constructor(editor: monaco.editor.ICodeEditor, props: IAddCommentGutterWidgetProps) {
        super(editor, props);

        this.disposables.push(
            this.editor.onMouseMove(e => {
                if (isMe(e.target.detail)) {
                    // If mouse is over the widgets itself, return to prevent flickering
                    return;
                }

                if (isAddCommentWidget(e.target.detail)) {
                    // If mouse is over add comment widget (used for selected text),
                    // hide the widget to prevent confusion
                    this.hide();
                    return;
                }

                const position = e.target.position;
                if (position) {
                    this.lineNumber = position.lineNumber;
                    const offsetTop = editor.getTopForLineNumber(this.lineNumber) - editor.getScrollTop();

                    const domNode = this.getDomNode();
                    domNode.style.top = offsetTop + "px";
                    domNode.style.margin = `0 ${this.calculateLeftMargin(this.lineNumber)}px`;
                } else {
                    this.hide();
                }
            })
        );

        // Hide on mouse leave
        this.disposables.push(this.editor.onMouseLeave(() => this.hide()));
    }

    public getId(): string {
        return `${this.editor.getId()}:${this.props.addToOriginalDiff ? "Original" : "Modified"}${componentType}`;
    }

    public getDomNode(): HTMLElement {
        if (!this.domNode) {
            this.domNode = document.createElement("div");
            this.domNode.style.top = hiddenTopStyle;

            const { addToOriginalDiff, diffOrientation, onAddDiscussion } = this.props;

            ReactDOM.render(
                <AddCommentButton
                    onClick={() => {
                        const lineInfo = {
                            lineEnd: this.lineNumber,
                            lineEndOffset: this.editor.getModel()!.getLineLength(this.lineNumber) + 1,
                            lineStart: this.lineNumber,
                            lineStartOffset: 1,
                            type: LineInfoType.Actual
                        };

                        const { selection } = this;
                        if (selection) {
                            // If adding to original side and inline diff mode, we don't honor selection
                            if (!addToOriginalDiff || diffOrientation != DiffViewerOrientation.Inline) {
                                // See the clicked line info is within the selection range, otherwise go with the clicked line info
                                if (lineInfo.lineStart >= selection.startLineNumber && lineInfo.lineEnd <= selection.endLineNumber) {
                                    lineInfo.lineEnd = selection.endLineNumber;
                                    lineInfo.lineEndOffset = selection.endColumn;
                                    lineInfo.lineStart = selection.startLineNumber;
                                    lineInfo.lineStartOffset = selection.startColumn;
                                }
                            }
                        }

                        onAddDiscussion(lineInfo, addToOriginalDiff);
                    }}
                />,
                this.domNode
            );
        }

        return this.domNode;
    }

    public getPosition(): monaco.editor.IOverlayWidgetPosition | null {
        // Returning null means the widget will position itself
        return null;
    }

    private hide(): void {
        this.lineNumber = 0;
        this.getDomNode().style.top = hiddenTopStyle;
    }

    private calculateLeftMargin(lineNumber: number): number {
        if (lineNumber < 10) {
            return 8;
        } else if (lineNumber < 100) {
            return 5;
        } else if (lineNumber < 1000) {
            return 2;
        }

        return 0;
    }
}
